import { createFileRoute, useParams } from "@tanstack/react-router";
import { useProject } from "@/hooks/use-project";
import { usePermissions } from "@/hooks/use-permissions";
import { usePendingRecordsCount } from "@/hooks/use-pending-records";
import { RecordsView } from "@/components/records/records-view/records-view";
import { EmptyRecordsState } from "@/components/records/empty-records-state/empty-records-state";

// Without `validateSearch`, TanStack Router strips unknown search params from
// navigations. The unpinned-photos-widget and report-mode deep-links pass these
// on click; declaring them here keeps them on the URL so the page can read
// them. See records-page.spec.md § URL params.
//
// `isReport=true` is the canonical report-mode param; `mode=report` is honored
// for back-compat for one release — see selection-mode-provider.tsx.
export interface RecordsSearchParams {
  has_pin?: boolean;
  plan?: string;
  isReport?: true;
  mode?: "report";
}

export const Route = createFileRoute("/projects/$projectId/records/")({
  component: RecordsPage,
  validateSearch: (search: Record<string, unknown>): RecordsSearchParams => {
    const out: RecordsSearchParams = {};
    if (search.has_pin === false || search.has_pin === "false") {
      out.has_pin = false;
    } else if (search.has_pin === true || search.has_pin === "true") {
      out.has_pin = true;
    }
    if (typeof search.plan === "string" && search.plan) out.plan = search.plan;
    if (search.isReport === true || search.isReport === "true") {
      out.isReport = true;
    }
    if (search.mode === "report") out.mode = "report";
    return out;
  },
});

export function RecordsPage() {
  const { projectId } = useParams({
    from: "/projects/$projectId/records/",
  });
  const { data: project } = useProject(projectId);
  // Queued offline uploads live in the local outbox, not in `records_count`.
  // The gate must see them or a zero-photo project hides the user's pending
  // captures until a record is created online. See [[records-page.spec]].
  const pendingCount = usePendingRecordsCount(projectId);
  const { can } = usePermissions(
    project
      ? { owner: project.owner.id, my_roles: project.my_roles }
      : { owner: "", my_roles: [] },
  );

  if (!project) return null;

  const hasRecords = (project.records_count ?? 0) > 0 || pendingCount > 0;

  if (!hasRecords) {
    return (
      <EmptyRecordsState
        projectName={project.name}
        canUpload={can("record:Create")}
      />
    );
  }

  return <RecordsView projectId={projectId} project={project} />;
}
